import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Form, ListGroup, Row } from 'react-bootstrap';
import { OrganizationsService } from '../../../service/organizations.service';
import { Organization } from '../../../service/types/types';
import { UserService } from '../../../service/users.service';

interface SimpleUser {
  rut: string;
  name: string;
}

export default function OrganizationManager() {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [users, setUsers] = useState<SimpleUser[]>([]);
  const [codigoNuevaOrg, setCodigoNuevaOrg] = useState('');
  const [nombreNuevaOrg, setNombreNuevaOrg] = useState('');
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [selectedRut, setSelectedRut] = useState('');

  const loadOrganizations = async () => {
    const data = await OrganizationsService.findAll();
    setOrganizations(data);
  };

  useEffect(() => {
    loadOrganizations();
    // Se listan usuarios de todas las organizaciones (sin filtrar por la del admin actual),
    // ya que esta pantalla sirve justamente para mover/asignar usuarios entre organizaciones.
    UserService.findBy({ excludeUsers: [], role: [], turno: [], localidad: [] }).then((data) =>
      setUsers(data.map((u) => ({ rut: u.rut, name: u.name }))),
    );
  }, []);

  const handleCreate = async () => {
    if (!codigoNuevaOrg.trim() || !nombreNuevaOrg.trim()) {
      alert('Ingrese código y nombre para la organización');
      return;
    }
    await OrganizationsService.create({ code: codigoNuevaOrg.trim(), name: nombreNuevaOrg.trim() });
    setCodigoNuevaOrg('');
    setNombreNuevaOrg('');
    await loadOrganizations();
  };

  const handleDelete = async (organization: Organization) => {
    if (!window.confirm(`¿Eliminar la organización "${organization.name}"?`)) return;
    await OrganizationsService.remove(organization.id);
    if (selectedOrg?.id === organization.id) setSelectedOrg(null);
    await loadOrganizations();
  };

  const openOrganization = async (organization: Organization) => {
    const detalle = await OrganizationsService.findOne(organization.id);
    setSelectedOrg(detalle);
  };

  const handleAddUsuario = async () => {
    if (!selectedOrg) return;
    if (!selectedRut) {
      alert('Seleccione un usuario');
      return;
    }
    const organization = await OrganizationsService.addUsuario(selectedOrg.id, selectedRut);
    setSelectedOrg(organization);
    setSelectedRut('');
    await loadOrganizations();
  };

  const handleRemoveUsuario = async (rut: string) => {
    if (!selectedOrg) return;
    const organization = await OrganizationsService.removeUsuario(selectedOrg.id, rut);
    setSelectedOrg(organization);
    await loadOrganizations();
  };

  return (
    <Card className="mb-3">
      <Card.Header>Organizaciones</Card.Header>
      <Card.Body>
        <Row>
          <Col md={5}>
            <div className="d-flex gap-2 mb-3">
              <Form.Control
                value={codigoNuevaOrg}
                onChange={(e) => setCodigoNuevaOrg(e.target.value)}
                placeholder="Código (ej: M4)"
                style={{ maxWidth: '9rem' }}
              />
              <Form.Control
                value={nombreNuevaOrg}
                onChange={(e) => setNombreNuevaOrg(e.target.value)}
                placeholder="Nombre de la organización"
              />
              <Button variant="success" onClick={handleCreate}>
                Crear
              </Button>
            </div>
            <ListGroup>
              {organizations.map((organization) => (
                <ListGroup.Item
                  key={organization.id}
                  active={selectedOrg?.id === organization.id}
                  className="d-flex justify-content-between align-items-center"
                  action
                  onClick={() => openOrganization(organization)}
                >
                  <span>
                    {organization.name} <span className="text-muted">({organization.code})</span>
                  </span>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(organization);
                    }}
                  >
                    Eliminar
                  </Button>
                </ListGroup.Item>
              ))}
              {organizations.length === 0 && (
                <ListGroup.Item className="text-center text-muted">No hay organizaciones creadas</ListGroup.Item>
              )}
            </ListGroup>
          </Col>
          <Col md={7}>
            {selectedOrg ? (
              <>
                <h6>Usuarios de "{selectedOrg.name}"</h6>
                <ListGroup className="mb-3">
                  {(selectedOrg.users ?? []).map((u) => (
                    <ListGroup.Item key={u.rut} className="d-flex justify-content-between align-items-center">
                      <span>
                        {u.name} ({u.rut})
                      </span>
                      <Button size="sm" variant="outline-danger" onClick={() => handleRemoveUsuario(u.rut)}>
                        Quitar
                      </Button>
                    </ListGroup.Item>
                  ))}
                  {(selectedOrg.users ?? []).length === 0 && (
                    <ListGroup.Item className="text-center text-muted">Sin usuarios asignados</ListGroup.Item>
                  )}
                </ListGroup>
                <div className="d-flex gap-2">
                  <Form.Select value={selectedRut} onChange={(e) => setSelectedRut(e.target.value)}>
                    <option value="">Seleccione un usuario</option>
                    {users.map((u) => (
                      <option key={u.rut} value={u.rut}>
                        {u.name} ({u.rut})
                      </option>
                    ))}
                  </Form.Select>
                  <Button variant="success" onClick={handleAddUsuario}>
                    Agregar
                  </Button>
                </div>
              </>
            ) : (
              <p className="text-muted">Seleccione una organización para administrar sus usuarios</p>
            )}
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
}
